import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Trophy, 
  Star, 
  TrendingUp, 
  Target,
  Sparkles,
  Award,
  Crown,
  Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface CareerStage {
  name: string;
  range: number[];
  color: string;
  description: string;
  icon?: React.ComponentType<any>;
}

interface ScoreRevealAnimationProps {
  totalScore: number;
  careerStage: CareerStage;
  onComplete: () => void;
}

const CAREER_STAGES = [
  { name: "Explorer", range: [0, 35], icon: Target, color: "text-career-explorer", description: "Building foundations" },
  { name: "Achiever", range: [36, 65], icon: TrendingUp, color: "text-career-achiever", description: "Developing expertise" },
  { name: "Specialist", range: [66, 89], icon: Star, color: "text-career-specialist", description: "Leading with expertise" },
  { name: "Authority", range: [90, 109], icon: Award, color: "text-career-authority", description: "Shaping industry" },
  { name: "Guru", range: [110, 120], icon: Crown, color: "text-career-guru", description: "Defining the future" }
];

const DIMENSION_NAMES = {
  technical: 'Technical Capability',
  industry: 'Industry Expertise', 
  leadership: 'Leadership Impact',
  communication: 'Communication Effectiveness',
  growth: 'Growth Trajectory'
};

export const ScoreRevealAnimation = ({ totalScore, careerStage, onComplete }: ScoreRevealAnimationProps) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [showCareerStage, setShowCareerStage] = useState(false);
  const [showActions, setShowActions] = useState(false);

  useEffect(() => {
    // Animated score counting
    const scoreInterval = setInterval(() => {
      setAnimatedScore(prev => {
        if (prev >= totalScore) {
          clearInterval(scoreInterval);
          setTimeout(() => setShowCareerStage(true), 500);
          return totalScore;
        }
        return prev + Math.ceil((totalScore - prev) / 10);
      });
    }, 50);

    return () => clearInterval(scoreInterval);
  }, [totalScore]);

  useEffect(() => {
    if (showCareerStage) {
      setTimeout(() => setShowActions(true), 1000);
    }
  }, [showCareerStage]);

  const getScoreColor = (score: number) => {
    if (score >= 90) return "text-accent-yellow";
    if (score >= 70) return "text-accent-green";
    if (score >= 50) return "text-accent-orange";
    return "text-accent-pink";
  };

  const getConfidenceMessage = (score: number) => {
    if (score >= 90) return "You're operating at an elite professional level with exceptional capabilities across multiple dimensions.";
    if (score >= 70) return "You demonstrate strong professional capabilities with clear areas of expertise and leadership.";
    if (score >= 50) return "You show solid professional competency with good growth potential and developing expertise.";
    return "You're building foundational skills with excellent opportunity for growth and development.";
  };

  const StageIcon = careerStage.icon || Star;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-purple-950/20 to-background flex items-center justify-center">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Score Reveal */}
          <Card className="p-12 text-center border-0 shadow-2xl bg-gradient-to-br from-card-bg to-card-hover relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-cosmic opacity-5"></div>
            <div className="relative z-10 space-y-8">
              <div className="space-y-4">
                <Sparkles className="w-12 h-12 mx-auto text-primary animate-pulse" />
                <h1 className="text-3xl md:text-4xl font-bold text-text-primary">
                  Your Loaded Score™
                </h1>
                <p className="text-text-secondary text-lg">
                  Your comprehensive professional capability metric
                </p>
              </div>

              <div className="space-y-6">
                <div className="relative">
                  <div className={cn(
                    "text-8xl md:text-9xl font-bold transition-all duration-1000",
                    getScoreColor(animatedScore),
                    "filter drop-shadow-2xl"
                  )}>
                    {animatedScore}
                  </div>
                  <div className="text-2xl text-text-muted font-medium">
                    out of 120
                  </div>
                </div>

                <div className="max-w-2xl mx-auto">
                  <p className="text-text-secondary leading-relaxed">
                    {getConfidenceMessage(totalScore)}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Career Stage */}
          {showCareerStage && (
            <Card className="p-8 border-0 shadow-xl bg-gradient-to-br from-primary/10 to-secondary/10 animate-scale-in">
              <div className="text-center space-y-6">
                <div className="space-y-4">
                  <StageIcon className={cn("w-16 h-16 mx-auto", careerStage.color)} />
                  <div>
                    <h2 className="text-3xl font-bold text-text-primary mb-2">
                      {careerStage.name}
                    </h2>
                    <p className="text-xl text-text-secondary">
                      {careerStage.description}
                    </p>
                  </div>
                </div>

                <Badge variant="outline" className="px-4 py-2 text-lg">
                  Score Range: {careerStage.range[0]} - {careerStage.range[1]}
                </Badge>

                <div className="bg-card-bg/50 rounded-xl p-6">
                  <p className="text-text-secondary leading-relaxed">
                    You're currently performing at the <strong className="text-text-primary">{careerStage.name}</strong> level. 
                    This places you in the {careerStage.range[0]}-{careerStage.range[1]} score range, indicating 
                    you're {careerStage.description.toLowerCase()}.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Actions */}
          {showActions && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
              <Button 
                size="lg" 
                onClick={onComplete}
                className="bg-gradient-primary hover:opacity-90 text-lg px-8 py-6"
              >
                <Trophy className="w-5 h-5 mr-2" />
                View Full Results
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => window.location.href = '/dashboard'}
                className="text-lg px-8 py-6"
              >
                <Zap className="w-5 h-5 mr-2" />
                Go to Dashboard
              </Button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};